import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/Auth.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000';

const OAUTH_ERRORS = {
  pending_approval: 'Your account is awaiting admin approval. You will be notified once approved.',
  account_disabled: 'Your account has been disabled. Please contact the admin.',
  google_auth_failed: 'Google sign-in failed. Please try again.',
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [needs2FA, setNeeds2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errCode = params.get('error');
    if (errCode && OAUTH_ERRORS[errCode]) {
      setError(OAUTH_ERRORS[errCode]);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password, needs2FA ? twoFactorCode : undefined);

      if (response.requires2FA) {
        setNeeds2FA(true);
        setLoading(false);
        return;
      }
      
      if (!response || !response.user) {
        setError('Invalid login response. Please try again.');
        return;
      }
      
      const userRole = response.user.role;
      
      if (userRole === 'teacher') {
        navigate('/teacher-dashboard');
      } else if (userRole === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/student-dashboard');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/api/auth/google`;
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Login</h2>
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={needs2FA}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={needs2FA}
          />
          {needs2FA && (
            <div className="two-factor-section">
              <p className="two-factor-hint">🔐 Enter the 6-digit code from your authenticator app</p>
              <input
                type="text"
                placeholder="6-digit 2FA code"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                autoFocus
                required
                style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.2em' }}
              />
            </div>
          )}
          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : needs2FA ? 'Verify & Login' : 'Login'}
          </button>
          {needs2FA && (
            <button type="button" className="back-btn" onClick={() => { setNeeds2FA(false); setTwoFactorCode(''); setError(''); }} style={{ marginTop: '8px', background: 'transparent', color: '#666', border: '1px solid #ddd' }}>
              ← Back to login
            </button>
          )}
        </form>

        <div className="divider">OR</div>

        <button className="google-btn" onClick={handleGoogleLogin}>
          🔐 Sign in with Google
        </button>

        <p>Don't have an account? <Link to="/signup">Sign Up</Link></p>
        <p><Link to="/forgot-password">Forgot Password?</Link></p>
      </div>
    </div>
  );
};

export default Login;
